import React, {useEffect, useState} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  Typography,
  useTheme,
} from '@mui/material';
import {
  COLOR_CHATHAMS_BLUE,
  COLOR_DENIM,
  COLOR_DISABLED_BG,
  COLOR_DISABLED_TEXT,
  COLOR_HUMMINGBIRD,
  COLOR_WHITE,
} from 'popTheme';
import { PopAppBarLoginOnly } from 'components/appBar/PopAppBar';
import { PopInputField } from 'components/form/PopInputField';
import { LoadingBackdrop } from 'components/home/LoadingBackdrop';
import backgroundImage from 'components/landing/background.svg';
import { Footer } from 'components/landing/Collaborators';
import { SettingsFormLabel } from 'components/settings/SettingsFormLabel';
import { clearResetPasswordWithTokenStatus } from 'redux/reducers/authorization';
import { sendNewPasswordWithResetTokenSaga } from 'redux/sagas/sendNewPasswordWithResetTokenSaga';
import { sendGetValidateToken } from '../../redux/sagas/validateTokenSaga';
import { selectIsValidToken } from '../../redux/reducers/validateTokenReducer';
import useLocalization from "localization/useLocalization";
import { standardCodeFromLanguageString } from "localization/useLocalization";

export const VALIDATION_PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*_+=`\-|(){}[\]:;"'<>,.?/])[A-Za-z\d~!@#$%^&*_+=`\-|(){}[\]:;"'<>,.?/]{8,}$/;

const PAUSE_TIME_BEFORE_BACK_TO_SIGNIN = 3000;
const ERROR_STATES = [404, 401, 400, 500];

export const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [formInput, setFormInput] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });
  const [formError, setFormError] = useState({
    newPassword: '',
    confirmNewPassword: '',
  });
  const [resetTokenFromEmail, setResetTokenFromEmail] = useState('');
  const [userId, setUserId] = useState(null);
  const { setPublicPageLanguage, locale  } = useLocalization();

  const handleSuccessfulResetPassword = () => {
    // Clear the form.
    setFormInput({
      newPassword: '',
      confirmNewPassword: '',
    });

    setTimeout(() => {
      navigate('/signin');
    }, PAUSE_TIME_BEFORE_BACK_TO_SIGNIN);
  };

  const isResetPasswordSuccess = useSelector(
    (state) => state?.authorization?.resetPasswordWithTokenResult?.success,
  );

  const resetPasswordWithTokenErrorCode = useSelector(
    (state) => state?.authorization?.resetPasswordWithTokenResult?.errorCode,
  );

  const loading = useSelector((state) => state.resourceDataReducer.loading);

  const messages = defineMessages({
    resetPasswordTitle: {
      id: 'resetPassword.title',
      defaultMessage: 'Reset Password',
      description: 'Reset password title text',
    },
    resetPasswordNetworkErrorTitle: {
      id: 'resetPassword.errortitle',
      defaultMessage: 'Oh no, failed to reset password.',
      description: 'Reset password title failed text',
    },
    resetPasswordButtonText: {
      id: 'resetPassword.button.text',
      defaultMessage: 'Reset Password',
      description: 'Reset password button text',
    },
    resetPasswordButtonFailedText: {
      id: 'resetPassword.button.failed.text',
      defaultMessage: 'Try Again',
      description: 'Reset password button failed text',
    },
    resetPasswordNewPassword: {
      id: 'resetPassword.new_password',
      defaultMessage: 'New Password',
      description: 'Reset password New Password field title',
    },
    resetPasswordConfirmNewPassword: {
      id: 'resetPassword.confirm_new_password',
      defaultMessage: 'Confirm New Password',
      description: 'Reset password Confirm New Password field title',
    },
    resetPasswordRules: {
      id: 'resetPassword.rules',
      defaultMessage:
        'Password must be at least 8 characters, including 1 capitalized letter, 1 small letter, 1 special character and 1 numeric character.',
      description: 'Settings page new password rules',
    },
    resetPasswordErrorFieldRequired: {
      id: 'resetPassword.error.field_required',
      defaultMessage: 'Field is required',
      description: 'Reset password error field required',
    },
    resetPasswordErrorPasswordsDoNotMatch: {
      id: 'resetPassword.error.passwords_do_not_match',
      defaultMessage: 'Passwords do not match.',
      description: 'Reset password error do not match',
    },
    resetPasswordErrorPasswordsPattern: {
      id: 'resetPassword.error.passwords_pattern',
      defaultMessage: 'Password does not meet requirements.',
      description: 'Reset password error pattern',
    },
    resetPasswordSuccessMessage: {
      id: 'resetPassword.success_message',
      defaultMessage: 'Successfully Reset Password',
      description: 'Reset password success message',
    },
    resetPasswordGenericErrorText: {
      id: 'resetPassword.genericerror.text',
      defaultMessage: "It's not you, it's the network.",
      description: 'Reset password generic error text',
    },
  });

  const validateField = (name, value) => {
    let newPasswordError = formError.newPassword;
    let confirmPasswordError = formError.confirmNewPassword;

    switch (name) {
      case 'newPassword':
        if (formInput.confirmNewPassword.length !== 0 && value !== formInput.confirmNewPassword) {
          confirmPasswordError = intl.formatMessage(messages.resetPasswordErrorPasswordsDoNotMatch);
        } else {
          confirmPasswordError = '';
        }

        if (value.length === 0 || VALIDATION_PASSWORD_REGEX.test(value)) {
          newPasswordError = '';
        } else {
          newPasswordError = intl.formatMessage(messages.resetPasswordErrorPasswordsPattern);
        }
        break;
      case 'confirmNewPassword':
        confirmPasswordError = '';
        if (value.length !== 0 && value !== formInput.newPassword) {
          confirmPasswordError = intl.formatMessage(messages.resetPasswordErrorPasswordsDoNotMatch);
        }
        break;
      default:
        break;
    }

    setFormError({
      newPassword: newPasswordError,
      confirmNewPassword: confirmPasswordError,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Clear the reset password status if necessary.
    if (isResetPasswordSuccess === true || !!resetPasswordWithTokenErrorCode) {
      dispatch(clearResetPasswordWithTokenStatus());
    }

    setFormInput(() => ({ ...formInput, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = (event) => {
    dispatch(sendNewPasswordWithResetTokenSaga(userId, formInput.newPassword, resetTokenFromEmail));
    event.preventDefault();
  };

  const handleClearNewPassword = (event) => {
    setFormInput({ ...formInput, newPassword: '' });
    setFormError({ ...formError, newPassword: '' });
  };

  const handleClearConfirmPassword = (event) => {
    setFormInput({ ...formInput, confirmNewPassword: '' });
    setFormError({ ...formError, confirmNewPassword: '' });
  };

  const handleSetLanguageIfNeeded = (language) => {
    const standardCodeFromLanguage = standardCodeFromLanguageString(language);

    // NOTE: Only set the language if it's different from the current language.
    if (locale !== standardCodeFromLanguage) {
      setPublicPageLanguage(standardCodeFromLanguage);
    }
  };

  useEffect(() => {
    if (isResetPasswordSuccess) {
      handleSuccessfulResetPassword();
    }
  });

  useEffect(() => {
    setResetTokenFromEmail(searchParams.get('token'));
    setUserId(searchParams.get('id'));

    dispatch(
      sendGetValidateToken({
        token: searchParams.get('token'),
      }),
    );
  }, [searchParams, dispatch]);

  const isValidToken = useSelector(selectIsValidToken);

  useEffect(() => {
    if (!isValidToken) {
      navigate('/invalidtoken');
    }
  }, [isValidToken, navigate]);

  useEffect(() => {
    const language = searchParams.get('language') || "ENGLISH";
    handleSetLanguageIfNeeded(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <Container
      sx={{
        bgcolor: COLOR_DENIM,
        height: '100%',
        borderRadius: '0.75rem',
        py: '2rem',
        [theme.breakpoints.only('xs')]: {
          my: '1rem',
        },
      }}
    >
      {loading && <LoadingBackdrop />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" textAlign="center" color={COLOR_WHITE} mb={'0.5rem'}>
          {intl.formatMessage(
            ERROR_STATES.indexOf(resetPasswordWithTokenErrorCode) === -1
              ? messages.resetPasswordTitle
              : messages.resetPasswordNetworkErrorTitle,
          )}
        </Typography>

        {ERROR_STATES.indexOf(resetPasswordWithTokenErrorCode) === -1 ? (
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth>
              <SettingsFormLabel
                subtitle={intl.formatMessage(messages.resetPasswordNewPassword)}
                isRequired={true}
                color={COLOR_WHITE}
                inputId="newPassword"
              />
              <PopInputField
                error={formError.newPassword?.length > 0}
                required
                fullWidth
                name="newPassword"
                type="password"
                id="newPassword"
                size="small"
                inputProps={{ 'data-testid': 'reset-password-new-password-input' }}
                onChange={handleInputChange}
                value={formInput.newPassword}
                onClickClearButton={handleClearNewPassword}
              />
              <FormHelperText error>{formError.newPassword}</FormHelperText>
              <Typography
                variant="subtitle1"
                color={COLOR_WHITE}
                mt={'1rem'}
                lineHeight={'1.21874rem'}
                align={'center'}
              >
                {intl.formatMessage(messages.resetPasswordRules)}
              </Typography>
            </FormControl>
            <FormControl fullWidth>
              <SettingsFormLabel
                subtitle={intl.formatMessage(messages.resetPasswordConfirmNewPassword)}
                isRequired={true}
                color={COLOR_WHITE}
              />
              <PopInputField
                error={formError.confirmNewPassword?.length > 0}
                required
                fullWidth
                name="confirmNewPassword"
                type="password"
                id="confirmNewPassword"
                size="small"
                inputProps={{ 'data-testid': 'reset-password-confirm-new-password-input' }}
                onChange={handleInputChange}
                value={formInput.confirmNewPassword}
                onClickClearButton={handleClearConfirmPassword}
              />
              <FormHelperText error={true}>{formError.confirmNewPassword}</FormHelperText>
            </FormControl>
            <FormControl fullWidth sx={{ alignItems: 'center' }} error={!isResetPasswordSuccess}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  !(
                    formError.newPassword?.length === 0 &&
                    formError.confirmNewPassword?.length === 0 &&
                    formInput.newPassword?.length > 0 &&
                    formInput.confirmNewPassword?.length > 0
                  )
                }
                data-testid="reset-password-button"
                sx={{
                  mt: '1rem',
                  mb: '1rem',
                  width: '20rem',
                  color: COLOR_DENIM,
                  bgcolor: COLOR_HUMMINGBIRD,
                  ':hover': {
                    opacity: 0.5,
                  },
                  '&.Mui-disabled': {
                    background: COLOR_DISABLED_BG,
                    color: COLOR_DISABLED_TEXT,
                  },
                }}
              >
                {intl.formatMessage(messages.resetPasswordButtonText)}
              </Button>
              <FormHelperText
                sx={{ display: isResetPasswordSuccess ? 'block' : 'none', color: COLOR_WHITE }}
              >
                {intl.formatMessage(messages.resetPasswordSuccessMessage)}
              </FormHelperText>
            </FormControl>
          </form>
        ) : resetPasswordWithTokenErrorCode === 400 ? (
          <>
            <Typography
              variant="subtitle1"
              color={COLOR_WHITE}
              mt={'1rem'}
              lineHeight={'1.21874rem'}
              align={'center'}
            >
              {intl.formatMessage(messages.resetPasswordErrorPasswordsPattern)}
            </Typography>
            <Button
              onClick={() => dispatch(clearResetPasswordWithTokenStatus())}
              variant="contained"
              data-testid="reset-password-button"
              sx={{
                mt: '1rem',
                mb: '1rem',
                width: '20rem',
                color: COLOR_DENIM,
                bgcolor: COLOR_HUMMINGBIRD,
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
              }}
            >
              {intl.formatMessage(messages.resetPasswordButtonFailedText)}
            </Button>
          </>
        ) : (
          <>
            <Typography
              variant="subtitle1"
              color={COLOR_WHITE}
              mt={'1rem'}
              lineHeight={'1.21874rem'}
              align={'center'}
            >
              {intl.formatMessage(messages.resetPasswordGenericErrorText)}
            </Typography>
            <Button
              onClick={() => dispatch(clearResetPasswordWithTokenStatus())}
              variant="contained"
              data-testid="reset-password-button"
              sx={{
                mt: '1rem',
                mb: '1rem',
                width: '20rem',
                color: COLOR_DENIM,
                bgcolor: COLOR_HUMMINGBIRD,
                ':hover': {
                  opacity: 0.5,
                },
                '&.Mui-disabled': {
                  background: COLOR_DISABLED_BG,
                  color: COLOR_DISABLED_TEXT,
                },
              }}
            >
              {intl.formatMessage(messages.resetPasswordButtonFailedText)}
            </Button>
          </>
        )}
      </Box>
    </Container>
  );
};

export const ResetPassword = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundImage: `url(${backgroundImage})`,
        height: '100%',
      }}
    >
      <Box xs={2} pt="3.75rem" sx={{ px: { xs: '1.5rem', sm: '3.75rem' } }}>
        <PopAppBarLoginOnly />
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        paddingBottom="3rem"
        width="100%"
        height="100vh"
      >
        <Box width="360px" alignSelf="flex-start" marginTop="3rem">
          <ResetPasswordForm />
        </Box>
      </Box>

      <Box
        xs={3}
        justifyContent="space-evenly"
        sx={{
          color: COLOR_CHATHAMS_BLUE,
          [theme.breakpoints.up('sm')]: {
            justifyContent: 'normal',
          },
        }}
      >
        <Footer />
      </Box>
    </Box>
  );
};